import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

// Each top-level nav link — with optional dropdown of sub-links.
const NAV_LINKS = [
  {
    to: "/farm",
    label: "About the Farm",
    submenu: [
      { to: "/farm", label: "The Story" },
      { to: "/gallery", label: "Walk the Land — Gallery" },
    ],
  },
  {
    to: "/stay",
    label: "Stay",
    submenu: [
      { to: "/stay", label: "Cottages" },
      { to: "/amenities", label: "Beyond the Cottage · Amenities" },
      { to: "/local-guide", label: "Local Guide" },
    ],
  },
  {
    to: "/shadow-foundation",
    label: "Shadow Foundation",
    submenu: [
      { to: "/shadow-foundation", label: "The Mission" },
      { to: "/shadow-foundation/apply", label: "Apply for a Stay" },
      { to: "/donate", label: "Donate" },
    ],
  },
  {
    to: "/new-leaf",
    label: "New Leaf",
    submenu: [
      { to: "/new-leaf", label: "Our Vision" },
      { to: "/new-leaf/interest", label: "Join the Interest List" },
    ],
  },
  { to: "/visit", label: "Contact" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null); // which top-level is showing its dropdown (mobile)
  const location = useLocation();
  const showDonate = location.pathname.startsWith("/shadow-foundation");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setOpenSubmenu(null);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-cream/90 backdrop-blur-xl border-b border-moss/10" : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group relative">
          <img
            src="/brand/iron-talon-logo-circle.png?v=3"
            alt="Iron Talon Farm"
            className={`transition-all duration-500 ${scrolled ? "h-11" : "h-14"} w-auto`}
          />
        </Link>

        {/* Desktop nav — bigger links with dropdowns */}
        <nav className="hidden lg:flex items-center gap-12">
          {NAV_LINKS.map((link) => (
            <div key={link.to} className="relative group" data-testid={`nav-item-${link.to.replace("/", "")}`}>
              <NavLink
                to={link.to}
                data-testid={`nav-link-${link.to.replace("/", "")}`}
                onClick={(e) => {
                  if (location.pathname === link.to) {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                }}
                className={({ isActive }) =>
                  `inline-flex items-center gap-1.5 text-[18px] tracking-wide font-medium transition-colors duration-300 whitespace-nowrap py-2 ${
                    isActive ? "text-moss" : "text-coal/85 hover:text-moss"
                  }`
                }
              >
                {link.label}
                {link.submenu && (
                  <ChevronDown size={15} strokeWidth={1.8} className="transition-transform duration-300 group-hover:rotate-180 opacity-70" />
                )}
              </NavLink>

              {/* Dropdown */}
              {link.submenu && (
                <div
                  className="absolute top-full left-1/2 -translate-x-1/2 pt-2 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-300 translate-y-1 group-hover:translate-y-0"
                  data-testid={`nav-dropdown-${link.to.replace("/", "")}`}
                >
                  <div className="bg-cream border border-moss/15 shadow-2xl min-w-[260px] py-3">
                    {link.submenu.map((sub) => (
                      <Link
                        key={sub.to}
                        to={sub.to}
                        onClick={(e) => {
                          if (location.pathname === sub.to.split("#")[0] && !sub.to.includes("#")) {
                            e.preventDefault();
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }
                        }}
                        className="block px-6 py-3 text-[16px] tracking-wide text-coal/80 hover:text-moss hover:bg-oat/50 transition-colors duration-200"
                        data-testid={`nav-sub-${sub.to.replace(/\//g, "-").replace(/#/g, "-")}`}
                      >
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {showDonate && (
            <Link
              to="/donate"
              data-testid="nav-donate-btn"
              className="hidden md:inline-flex items-center bg-moss text-cream px-5 py-2.5 text-xs tracking-[0.18em] uppercase font-medium hover:bg-ink transition-colors duration-500"
            >
              Donate
            </Link>
          )}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 -mr-2 text-moss"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} strokeWidth={1.4} /> : <Menu size={22} strokeWidth={1.4} />}
          </button>
        </div>
      </div>

      {/* Mobile menu — accordion for submenus */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-500 bg-cream border-t border-moss/10 ${
          open ? "max-h-[800px]" : "max-h-0"
        }`}
        data-testid="mobile-menu"
      >
        <div className="container-x py-6 flex flex-col gap-4">
          {NAV_LINKS.map((link) => (
            <div key={link.to}>
              <div className="flex items-center justify-between">
                <NavLink
                  to={link.to}
                  className="font-serif text-2xl text-moss"
                  data-testid={`mobile-nav-link-${link.to.replace("/", "")}`}
                >
                  {link.label}
                </NavLink>
                {link.submenu && (
                  <button
                    onClick={() => setOpenSubmenu(openSubmenu === link.to ? null : link.to)}
                    className="p-2 text-moss"
                    aria-label={`Toggle ${link.label} submenu`}
                    data-testid={`mobile-nav-toggle-${link.to.replace("/", "")}`}
                  >
                    <ChevronDown size={18} strokeWidth={1.6} className={`transition-transform ${openSubmenu === link.to ? "rotate-180" : ""}`} />
                  </button>
                )}
              </div>
              {link.submenu && openSubmenu === link.to && (
                <div className="mt-2 ml-4 space-y-2 pl-3 border-l border-moss/20">
                  {link.submenu.map((sub) => (
                    <Link
                      key={sub.to}
                      to={sub.to}
                      className="block text-base text-coal/75 hover:text-moss"
                      data-testid={`mobile-nav-sub-${sub.to.replace(/\//g, "-").replace(/#/g, "-")}`}
                    >
                      {sub.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          {showDonate && (
            <Link to="/donate" className="btn-primary w-full mt-4" data-testid="mobile-nav-donate-btn">
              Donate to The Shadow Foundation
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
